import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ClerkProvider } from '@clerk/clerk-react';
import { AuthProvider } from '@/hooks/useClerkAuth';
import { CLERK_PUBLISHABLE_KEY } from '@/lib/clerk';
import ErrorBoundary from '@/components/ErrorBoundary';
import { WebKitErrorBoundary } from '@/components/WebKitErrorBoundary';
import SmartAuthFlow from '@/components/SmartAuthFlow';
import Index from './pages/Index';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import AuthError from './pages/AuthError';
import MainDashboard from './pages/MainDashboard';
import ArticleManager from './pages/ArticleManager';
import ArticleCreator from './pages/ArticleCreator';
import UserManagement from './pages/UserManagement';
import EditArticle from './pages/EditArticle';
import Progress from './pages/Progress';
import Settings from './pages/Settings';
import Questions from './pages/Questions';
import QuestionPractice from './pages/QuestionPractice';
import QuestionEditor from './pages/QuestionEditor';
import NotFound from './pages/NotFound';
import './i18n';
import PricingPage from './pages/Pricing';

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <ErrorBoundary>
      <WebKitErrorBoundary>
        <ClerkProvider
          publishableKey={CLERK_PUBLISHABLE_KEY}
          navigate={to => {
            // Handle navigation after sign-in/sign-up
            if (typeof window !== 'undefined') {
              window.history.pushState(null, '', to);
              window.dispatchEvent(new Event('popstate'));
            }
          }}
          signInFallbackRedirectUrl="/"
          signUpFallbackRedirectUrl="/"
        >
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <ErrorBoundary>
                  <BrowserRouter>
                    <SmartAuthFlow>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route
                          path="/home"
                          element={<Navigate to="/" replace />}
                        />
                        <Route path="/sign-in" element={<SignIn />} />
                        <Route path="/sign-up" element={<SignUp />} />
                        <Route path="/auth" element={<SignIn />} />
                        <Route path="/auth-error" element={<AuthError />} />
                        <Route path="/dashboard" element={<MainDashboard />} />
                        <Route path="/progress" element={<Progress />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route
                          path="/manage-articles"
                          element={<ArticleManager />}
                        />
                        <Route
                          path="/create-article"
                          element={<ArticleCreator />}
                        />
                        <Route
                          path="/manage-users"
                          element={<UserManagement />}
                        />
                        <Route
                          path="/edit-article/:id"
                          element={<EditArticle />}
                        />
                        <Route path="/questions" element={<Questions />} />
                        <Route
                          path="/questions/:catalogId/practice"
                          element={<QuestionPractice />}
                        />
                        <Route
                          path="/questions/:catalogId/edit"
                          element={<QuestionEditor />}
                        />
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </SmartAuthFlow>
                  </BrowserRouter>
                </ErrorBoundary>
              </TooltipProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ClerkProvider>
      </WebKitErrorBoundary>
    </ErrorBoundary>
  </HelmetProvider>
);

export default App;
